<script setup lang="ts">
import {
  computed,
  ref,
  useUser,
  useDialog,
  useToast,
  callOnce,
} from "#imports";
import { useBookmarks } from "~/stores/bookmarks";
import MPAuth from "../dialogs/MPAuth/MPAuth.vue";
import MPButton from "../forms/MPButton.vue";
import MPTooltip from "./MPTooltip.vue";

const props = withDefaults(
  defineProps<{
    id: string;
    bookmark: "map_bookmarks" | "spot_bookmarks";
    version?: string;
    backgroundColor?: string;
    iconColor?: string;
    size?: "small" | "medium" | "large";
    label?: string;
    rounded?: boolean;
    link?: boolean;
    hideLabel?: boolean;
    tooltip?: boolean;
  }>(),
  {
    bookmark: "map_bookmarks",
    version: undefined,
    backgroundColor: undefined,
    iconColor: undefined,
    size: "medium",
    label: undefined,
    rounded: false,
    link: false,
    hideLabel: false,
    tooltip: false,
  },
);

const emits = defineEmits<{
  completed: [];
}>();

const { showError } = useToast();
const { openDialog } = useDialog();
const userStore = useUser();
const bookmarkStore = useBookmarks();
const isLoading = ref(false);
const showTooltip = ref(false);
const toolTipContent = ref("");

await callOnce(async () => {
  bookmarkStore.fetchMapBookmarks();
  bookmarkStore.fetchSpotBookmarks();
});

const isBookmarked = computed(() => {
  if (props.bookmark === "map_bookmarks") {
    return bookmarkStore.isMapBookmarked(props.id).value;
  } else if (props.bookmark === "spot_bookmarks") {
    return bookmarkStore.isSpotBookmarked(props.id).value;
  }
  return false;
});

const addBookmark = async () => {
  try {
    isLoading.value = true;
    if (props.bookmark === "map_bookmarks") {
      await bookmarkStore.addMapBookmark(props.id);
    } else if (props.bookmark === "spot_bookmarks") {
      await bookmarkStore.addSpotBookmark(props.id);
    }
    toolTipContent.value = "Saved to atlas";
    showTooltip.value = true;
    setTimeout(() => {
      showTooltip.value = false;
      toolTipContent.value = "";
    }, 500);
    emits("completed");
  } catch (e) {
    showError("Unable to add bookmark", e);
  } finally {
    isLoading.value = false;
  }
};

const removeBookmark = async () => {
  try {
    isLoading.value = true;
    if (props.bookmark === "map_bookmarks") {
      await bookmarkStore.removeMapBookmark(props.id);
    } else if (props.bookmark === "spot_bookmarks") {
      await bookmarkStore.removeSpotBookmark(props.id);
    }
    emits("completed");
  } catch (e) {
    showError("Unable to remove bookmark", e);
  } finally {
    isLoading.value = false;
  }
};
</script>
<template>
  <div v-if="props.bookmark === 'map_bookmarks'" :class="props.bookmark">
    <div v-if="userStore.user.id">
      <MPButton
        v-if="isLoading"
        :link="props.link"
        :size="props.size"
        :rounded="rounded"
        icon="ri-heart-add-line"
        :hideLabel="props.hideLabel"
        version="secondary"
      />
      <MPButton
        v-else-if="!isBookmarked"
        label="Save to Atlas"
        icon="ri-heart-add-line"
        :hideLabel="props.hideLabel"
        version="secondary"
        :rounded="rounded"
        :size="props.size"
        @click="addBookmark"
      />
      <MPButton
        v-else
        :link="props.link"
        label="Saved to Atlas"
        icon="ri-heart-fill"
        version="secondary"
        :hideLabel="props.hideLabel"
        :rounded="rounded"
        :size="props.size"
        @click="removeBookmark"
      />
    </div>
    <MPButton
      v-else
      :link="props.link"
      :size="props.size"
      label="Save to Atlas"
      iconColor="var(--color-slate-800)"
      icon="ri-heart-add-line"
      :rounded="rounded"
      :hideLabel="props.hideLabel"
      version="secondary"
      @click="openDialog(MPAuth, { startOn: 'login' })"
    />
  </div>
  <div v-else-if="props.bookmark === 'spot_bookmarks'" :class="props.bookmark">
    <div v-if="userStore.user.id">
      <MPButton
        v-if="isLoading"
        :size="props.size"
        :link="props.link"
        :rounded="props.rounded"
        :backgroundColor="props.backgroundColor"
        version="primary"
        icon="ri-loader-line"
        iconColor="white"
        label="isLoading"
        :hideLabel="props.hideLabel"
      />
      <MPButton
        v-else-if="!isBookmarked"
        :size="props.size"
        :link="props.link"
        icon="ri-heart-add-line"
        :rounded="rounded"
        iconColor="white"
        version="primary"
        :backgroundColor="props.backgroundColor"
        label="Save to Atlas"
        :hideLabel="props.hideLabel"
        @click="addBookmark"
      />
      <MPButton
        v-else
        :size="props.size"
        :link="props.link"
        :rounded="rounded"
        :backgroundColor="props.backgroundColor"
        version="primary"
        icon="ri-heart-fill"
        label="Saved to Atlas"
        :hideLabel="props.hideLabel"
        @click="removeBookmark"
      />
    </div>
    <MPButton
      v-else
      version="primary"
      :backgroundColor="props.backgroundColor"
      :size="props.size"
      :rounded="rounded"
      :link="props.link"
      icon="ri-heart-add-line"
      label="Save to Atlas"
      :hideLabel="props.hideLabel"
      @click="openDialog(MPAuth, { startOn: 'login' })"
    />
    <MPTooltip
      v-if="props.tooltip"
      :content="toolTipContent"
      mode="fade"
      :show="showTooltip"
      class="tooltip-placement"
    />
  </div>
</template>
<style scoped>
.tooltip-placement {
  position: absolute;
  bottom: 110%;
  left: 50%;
  transform: translateX(-50%);
}

.map_bookmarks {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.spot_bookmarks {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.spot_bookmarks :deep(button) {
  border: none !important;
}

.spot_bookmarks :deep(i) {
  border-radius: 100%;
  color: v-bind('props.iconColor || "var(--color-black)"');
}
</style>
